import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/hooks/useAuth';
import { useConversationThread } from '@/hooks/useConversationThread';
import { useToast } from '@/hooks/use-toast';
import { Heart, Send, MessageCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchAnnouncementReplyTimeline } from '@/lib/replyFeed';

interface ReplyThreadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  announcementId: string | null;
  recipientName?: string;
}

interface DirectReply {
  id: string;
  reply_text: string;
  created_at: string;
}

interface AnnouncementPreview {
  id: string;
  recipient_name: string;
  message: string | null;
  template_name: string | null;
  public_slug: string | null;
}

const READ_KEY = 'sin_replies_read_v1';

function markRead(ids: string[]) {
  try {
    const raw = localStorage.getItem(READ_KEY);
    const set = new Set<string>(raw ? JSON.parse(raw) : []);
    ids.forEach((id) => set.add(id));
    localStorage.setItem(READ_KEY, JSON.stringify(Array.from(set).slice(-500)));
  } catch {}
}

export function isReplyRead(id: string): boolean {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as string[]).includes(id);
  } catch {
    return false;
  }
}

export function ReplyThreadDialog({
  open,
  onOpenChange,
  announcementId,
  recipientName,
}: ReplyThreadDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [announcement, setAnnouncement] = useState<AnnouncementPreview | null>(null);
  const [directReplies, setDirectReplies] = useState<DirectReply[]>([]);
  const [threadTimelineMessages, setThreadTimelineMessages] = useState<Array<{
    id: string;
    thread_id: string;
    sender_role: 'owner' | 'participant';
    message: string;
    created_at: string;
  }>>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { thread, messages, isSending, sendMessage, startThread } =
    useConversationThread(announcementId || '', true);

  useEffect(() => {
    if (!open || !announcementId || !user) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const timeline = await fetchAnnouncementReplyTimeline(user.id, announcementId);
        if (cancelled) return;
        setAnnouncement(timeline.announcement);
        setDirectReplies(timeline.directReplies);
        setThreadTimelineMessages(timeline.threadMessages);

        // Mark all visible replies as read
        const ids = [
          ...timeline.directReplies.map((r) => r.id),
          ...timeline.threadMessages.filter((m) => m.sender_role === 'participant').map((m) => m.id),
        ];
        if (ids.length) markRead(ids);
      } catch (err) {
        console.error('Failed to load reply thread:', err);
        if (!cancelled) {
          setAnnouncement(null);
          setDirectReplies([]);
          setThreadTimelineMessages([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, announcementId, user]);

  useEffect(() => {
    if (messages.length) {
      markRead(messages.filter((m) => m.sender_role === 'participant').map((m) => m.id));
    }
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, directReplies, threadTimelineMessages, open]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !announcementId) return;

    if (thread) {
      const ok = await sendMessage(text);
      if (ok) {
        setDraft('');
        toast({ title: 'Reply sent', description: 'They\'ll see it when they reopen the message.' });
      } else {
        toast({ title: 'Could not send', description: 'Please try again.', variant: 'destructive' });
      }
    } else {
      // No thread yet — owner can't initiate; show guidance
      toast({
        title: 'Reply not available yet',
        description: 'You can reply once they start a conversation from your announcement.',
      });
    }
  };

  const canReply = !!thread;

  // Build a unified timeline
  type Item =
    | { kind: 'direct'; id: string; text: string; at: string; from: 'participant' }
    | { kind: 'thread'; id: string; text: string; at: string; from: 'owner' | 'participant' };

  const timeline: Item[] = [
    ...directReplies.map<Item>((r) => ({
      kind: 'direct',
      id: r.id,
      text: r.reply_text,
      at: r.created_at,
      from: 'participant',
    })),
    ...[...threadTimelineMessages, ...messages]
      .filter((m, index, arr) => arr.findIndex((candidate) => candidate.id === m.id) === index)
      .map<Item>((m) => ({
      kind: 'thread',
      id: m.id,
      text: m.message,
      at: m.created_at,
      from: m.sender_role,
    })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col">
        <DialogHeader className="px-4 sm:px-5 pt-5 pb-3 border-b border-border/60">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <MessageCircle className="h-4 w-4 text-primary" />
            </div>
            <span className="truncate">
              {announcement?.recipient_name || recipientName || 'Conversation'}
            </span>
          </DialogTitle>
          {announcement?.message && (
            <p className="text-xs text-muted-foreground italic line-clamp-2 pl-9">
              "{announcement.message}"
            </p>
          )}
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div ref={scrollRef} className="px-4 sm:px-5 py-4 space-y-3">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading && timeline.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">
                No messages in this conversation yet.
              </p>
            )}

            {timeline.map((item, i) => {
              const mine = item.from === 'owner';
              return (
                <motion.div
                  key={`${item.kind}-${item.id}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.2) }}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                      mine
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : 'bg-muted/70 text-foreground rounded-bl-sm'
                    }`}
                  >
                    {!mine && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <Heart className="h-3 w-3 text-primary" fill="currentColor" />
                        <span className="text-[11px] font-medium opacity-80">
                          {announcement?.recipient_name || 'Them'}
                        </span>
                      </div>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {item.text}
                    </p>
                    <p
                      className={`text-[10px] mt-1 ${
                        mine ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}
                    >
                      {new Date(item.at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="border-t border-border/60 p-3 sm:p-4 bg-background">
          {canReply ? (
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write back from the heart…"
                rows={2}
                className="resize-none rounded-xl text-sm min-h-[44px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                onClick={handleSend}
                disabled={!draft.trim() || isSending}
                size="icon"
                className="h-11 w-11 shrink-0 rounded-xl"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          ) : (
            <div className="rounded-xl bg-muted/40 px-3 py-2.5 text-center">
              <p className="text-xs text-muted-foreground">
                {directReplies.length > 0
                  ? 'This reply was sent anonymously — no return thread to respond to.'
                  : 'A private thread will open here when they reply.'}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
